import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import Switch from "react-switch";
import moment from "moment";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSelector } from "react-redux";
import { hasPermission } from "utils/permissionHelper";
import { PERMISSIONS } from "constants/permissionTypes";
import { MODULES } from "constants/modulesTypes";
import { useNavigate, useLocation } from "react-router-dom";

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer";
import MoveStudentModal from "../../components/Common/MoveStudentModal";

const DatatableTables = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Desc");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "all");
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem("authUser"));
  const role = user?.Role;
  const fileInputRef = React.useRef(null);

  const Offsymbol = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          fontSize: 12,
          color: "#fff",
          paddingRight: 2
        }}
      >
        {" "}
        No
      </div>
    );
  };

  const OnSymbol = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          fontSize: 12,
          color: "#fff",
          paddingRight: 2
        }}
      >
        {" "}
        Yes
      </div>
    );
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const todayDate = moment().format("YYYY-MM-DD");
      const query = new URLSearchParams({
        page,
        countPerPage,
        sortingBy,
        direction,
        statusFilter,
        search,
      }).toString();

      let apiUrl = "";

      switch (activeTab) {
        case "new":
          apiUrl = `https://api.kunalsdrumclass.com/api/GetNewStudents?${query}`;
          break;

        case "pause":
          apiUrl = `https://api.kunalsdrumclass.com/api/GetAllStudentPause?date=${todayDate}&${query}`;
          break;

        case "rejected":
          apiUrl = `https://api.kunalsdrumclass.com/api/GetRejectedStudents?${query}`;
          break;

        default:
          apiUrl = `https://api.kunalsdrumclass.com/api/GetAllStudents?date=${todayDate}&${query}`;
      }

      const res = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;

      if (list.length === 0 && page > 1) {
        setPage(1);
        return;
      }

      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );

      setTotalRecords(total);
    } catch (error) {
      console.error("Error loading students:", error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, countPerPage, sortingBy, direction, statusFilter, search, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const permissionMap = useSelector(
    state => state.permissions?.permissions || {}
  );

  const handleApprove = async (rowData) => {
    setLoading(true);
    try {
      await fetch("https://api.kunalsdrumclass.com/api/UpdateApproveStatus", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: rowData.Id, loginId: rowData.Login_Id, isApproved: true, createdBy: user.Id }),
      });

      await Swal.fire({
        title: "Success!",
        text: `student approved successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      fetchData();
    } catch {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (rowData) => {
    setLoading(true);
    try {
      await fetch("https://api.kunalsdrumclass.com/api/UpdateApproveStatus", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: rowData.Id, loginId: rowData.Login_Id, isApproved: false, createdBy: user.Id }),
      });

      await Swal.fire({
        title: "Success!",
        text: `student rejected successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      fetchData();
    } catch {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to delete this student?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;
    try {
      const payload = { id: rowData.Id, modifiedBy: user.Id };
      await fetch(`https://api.kunalsdrumclass.com/api/DeleteStudentById?id=${payload.id}&modifiedBy=${payload.modifiedBy}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      await Swal.fire({
        title: "Success!",
        text: `student deleted successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  };

  const handleRevert = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to revert this student?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;
    try {
      const payload = { id: rowData.Login_Id, modifiedBy: user.Id };
      await fetch(`https://api.kunalsdrumclass.com/api/RevertStudentById?id=${payload.id}&modifiedBy=${payload.modifiedBy}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      await Swal.fire({
        title: "Success!",
        text: `student reverted successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  };

  const handleComp = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to give comp class?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;

    try {
      const now = new Date();
      const missedDate = now.toISOString().split("T")[0];
      const missedDay = now.toLocaleDateString("en-US", {
        weekday: "long",
      });

      const formatHour = (hour) => {
        const period = hour >= 12 ? "PM" : "AM";
        const formattedHour = hour % 12 || 12;
        return `${formattedHour}:00${period}`;
      };

      const currentHour = now.getHours();
      const startTime = formatHour(currentHour);
      const endTime = formatHour(currentHour + 1);
      const missedTime = `${startTime} - ${endTime}`;

      const payload = { studentId: rowData.Id, missedDate: missedDate, missedDay: missedDay, missedTime: missedTime };
      await fetch(`https://api.kunalsdrumclass.com/api/InsertStudentMissedClass`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      await Swal.fire({
        title: "Success!",
        text: `1 comp class given successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  };

  const handleStatusToggle = useCallback(async (rowData, checked) => {
    try {
      setTableData(prev =>
        prev.map(item =>
          item.Id === rowData.Id
            ? { ...item, LoginAllow: checked }
            : item
        )
      );

      const payload = {
        id: rowData.Login_Id,
        loginAllow: checked,
        userId: user.Id
      };

      const res = await fetch("https://api.kunalsdrumclass.com/api/UpdateLoginStatus",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();
      const status = data?.recordsets?.[0]?.[0]?.Status;

      if (status !== 1) {
        throw new Error("Failed");
      }

      await Swal.fire({
        title: "Success!",
        text: `Login status updated successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  }, [fetchData, token, user.Id]);

  const handleImportExcel = async (e) => {

    const file = e.target.files[0];

    if (!file) return;

    try {

      setImporting(true);

      const buffer = await file.arrayBuffer();

      const workbook = XLSX.read(buffer);

      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const rows = XLSX.utils.sheet_to_json(sheet);

      if (!rows.length) {

        Swal.fire("Error", "Excel is empty.", "error");
        return;

      }

      const students = rows.map(r => ({
        studentName: r["Student Name"] || "",
        parentName: r["Parent Name"] || "",
        emailAddress: r["Email"] || "",
        dateOfBirth: r["Date Of Birth"]
          ? moment(r["Date Of Birth"], "DD-MM-YYYY", true).format("YYYY-MM-DD")
          : "",
        contactNumber: String(r["Contact Number"] || ""),
        alternateNumber: String(r["Alternate Contact"] || ""),
        address: r["Address"] || "",
        instrument: r["Instrument"] || "",
        lessonPlan: r["Lesson Plan"] || "",
        institutionType: r["Institution Type"] || "",
        institutionName: r["Institution Name"] || "",
        institutionAddress: r["Institution Address"] || "",
        trinityExam: r["Trinity Exam"] || "",
        heardFrom: r["Heard From"] || "",

        classDuration: Number(r["Class Duration"] || 1),
        startDate: r["Start Date"]
          ? moment(r["Start Date"], "DD-MM-YYYY", true).format("YYYY-MM-DD")
          : "",
        endDate: r["End Date"]
          ? moment(r["End Date"], "DD-MM-YYYY", true).format("YYYY-MM-DD")
          : "",

        day1: r["Day 1"] || "",
        time1: r["Time 1"] || "",
        day2: r["Day 2"] || "",
        time2: r["Time 2"] || "",

        totalAmount: Number(r["Total Amount"] || 0),
        advanceAmount: Number(r["Advance Amount"] || 0),
        advanceStartDate: r["Advance Start Date"]
          ? moment(r["Advance Start Date"], "DD-MM-YYYY", true).format("YYYY-MM-DD")
          : "",
        advanceEndDate: r["Advance End Date"]
          ? moment(r["Advance End Date"], "DD-MM-YYYY", true).format("YYYY-MM-DD")
          : "",
        advanceDuration: Number(r["Advance Duration"] || 0)
      }));

      const response = await fetch(
        "https://api.kunalsdrumclass.com/api/BulkInsertStudents",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            students
          })
        }
      );

      const result = await response.json();
      const records = result.records || [];
      const success = records.filter(x => x.status === 1);
      const failed = records.filter(x => x.status === 0);

      const alert = await Swal.fire({
        icon: failed.length ? "warning" : "success",
        title: "Import Completed",
        html: `
      <div style="text-align:left;font-size:15px">
          <p><b>Total Records :</b> ${records.length}</p>
          <p style="color:green"><b>✅ Success :</b> ${success.length}</p>
          <p style="color:red"><b>❌ Failed :</b> ${failed.length}</p>
      </div>
  `,
        showCancelButton: failed.length > 0,
        confirmButtonText: "Close",
        cancelButtonText: "Download Failed Records",
        reverseButtons: true,
      });

      if (alert.dismiss === Swal.DismissReason.cancel) {
        downloadFailedRecords(failed);
      }

      if (success.length > 0) {
        fetchData();
      }

    }
    catch (err) {

      console.error(err);

      Swal.fire(
        "Error",
        "Import failed.",
        "error"
      );

    }
    finally {

      setImporting(false);

      e.target.value = "";

    }

  };

  const downloadFailedRecords = (failedRecords) => {
    if (!failedRecords.length) return;

    const exportData = failedRecords.map((item) => ({
      Row: item.row,
      Message: item.message || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    worksheet["!cols"] = [
      { wch: 10 },
      { wch: 60 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Records");

    XLSX.writeFile(workbook, "FailedStudents.xlsx");
  };

  const getExportStudentData = async () => {
    try {
      setLoading(true);

      const todayDate = moment().format("YYYY-MM-DD");
      const res = await fetch(`https://api.kunalsdrumclass.com/api/GetAllStudents?date=${todayDate}&page=1&countPerPage=1000&sortingBy=Id&direction=Desc&statusFilter=${statusFilter}&search=`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await res.json();

      const students = result?.recordsets?.[0] || [];

      if (!statusFilter) {
        return students;
      }

      return students.filter((student) => {
        const isMidway =
          student.Start_Date &&
          new Date(student.Start_Date).getDate() !== 1;

        const isExpired = student.Plan_IsActive === null;

        if (statusFilter === "midway") {
          return isMidway;
        }

        if (statusFilter === "expired") {
          return isExpired;
        }

        return true;
      });
    } catch (error) {
      console.error("Export Error:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Excel Export
  const exportToExcel = async () => {
    const exportRows = await getExportStudentData();

    if (!exportRows.length) return;

    const exportData = exportRows.map((item) => ({
      Id: item.Id,
      "Student Name": item.Student_Name || "-",
      "Parent Name": item.Parent_Name || "-",
      "Date Of Birth": item.Date_Of_Birth
        ? moment(item.Date_Of_Birth).format("DD-MM-YYYY")
        : "-",
      "Contact Number": item.Contact_Number || "-",
      "Alternate/Emergency Contact Number":
        item.Alternate_Number || "-",
      "Complete Address": item.Address || "-",
      Email: item.Email_Address || "-",
      Instrument: item.Instrument || "-",
      "Lesson Plan": item.Lesson_Plan || "-",
      Duration: `${item.Class_Duration || 0} Month`,
      Day: item.Day_Of_Week || "-",
      Time: item.Time || "-",
      "Start Date": item.Start_Date
        ? moment(item.Start_Date).format("DD-MM-YYYY")
        : "-",
      "End Date": item.End_Date
        ? moment(item.End_Date).format("DD-MM-YYYY")
        : "-",
      "Total Amount": item.Total_Amount || "0.00",
      "Institution Type": item.Institution_Type || "-",
      "Institution Name": item.Institution_Name || "-",
      "Institution Address": item.Institution_Address || "-",
      "Trinity Exam": item.Trinity_Exam || "-",
      "Heard From": item.Heard_From || "-",
      "Status":
        item.Plan_IsActive === null
          ? "Plan Expired"
          : new Date(item.Start_Date).getDate() !== 1
            ? "Midway Student"
            : "Regular Student",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const columnWidths = Object.keys(exportData[0]).map((key) => ({
      wch: Math.max(
        key.length,
        ...exportData.map((row) =>
          row[key] ? row[key].toString().length : 10
        )
      ),
    }));

    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "StudentReport");

    XLSX.writeFile(workbook, "StudentReport.xlsx");
  };

  // PDF Export
  const exportToPDF = async () => {
    const exportRows = await getExportStudentData();

    if (!exportRows.length) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a3",
    });

    doc.text("Student Report", 5, 15);

    const tableColumn = [
      "Id",
      "Student Name",
      "Parent Name",
      "Date Of Birth",
      "Contact Number",
      "Alternate/Emergency Contact Number",
      "Complete Address",
      "Email",
      "Instrument",
      "Lesson Plan",
      "Duration",
      "Day",
      "Time",
      "Start Date",
      "End Date",
      "Total Amount",
      "Institution Type",
      "Institution Name",
      "Institution Address",
      "Trinity Exam",
      "Heard From",
      "Status"
    ];

    const tableRows = exportRows.map((item) => [
      item.Id,
      item.Student_Name || "-",
      item.Parent_Name || "-",
      item.Date_Of_Birth
        ? moment(item.Date_Of_Birth).format("DD-MM-YYYY")
        : "-",
      item.Contact_Number || "-",
      item.Alternate_Number || "-",
      item.Address || "-",
      item.Email_Address || "-",
      item.Instrument || "-",
      item.Lesson_Plan || "-",
      `${item.Class_Duration || 0} Month`,
      item.Day_Of_Week || "-",
      item.Time || "-",
      item.Start_Date
        ? moment(item.Start_Date).format("DD-MM-YYYY")
        : "-",
      item.End_Date
        ? moment(item.End_Date).format("DD-MM-YYYY")
        : "-",
      item.Total_Amount || "0.00",
      item.Institution_Type || "-",
      item.Institution_Name || "-",
      item.Institution_Address || "-",
      item.Trinity_Exam || "-",
      item.Heard_From || "-",
      item.Plan_IsActive === null
        ? "Plan Expired"
        : new Date(item.Start_Date).getDate() !== 1
          ? "Midway Student"
          : "Regular Student",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [22, 160, 133],
        fontSize: 8,
      },
      theme: "grid",
      margin: { top: 15, left: 5, right: 5, },
      tableWidth: "auto",
    });

    doc.save("StudentReport.pdf");
  };

  const columns = useMemo(() => {
    if (activeTab === "pause") {
      return [
        {
          header: "Id",
          accessorKey: "Student_Id",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Student Name",
          meta: {
            sticky: true,
            width: "125px",
          },
          accessorKey: "Student_Name",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Contact No",
          accessorKey: "Contact_Number",
          enableSorting: true,
          enableColumnFilter: false,
          cell: ({ row }) => {
            const rowData = row.original;
            const mobile = rowData.Contact_Number;
            const cleanNumber = mobile.replace(/\D/g, "");
            const email = rowData.Email_Address;

            return (
              <div className="d-flex flex-column gap-1">
                <span>{rowData.Contact_Number}</span>
                <div className="d-flex gap-2 align-items-center">
                  <a
                    href={`tel:+${cleanNumber}`}
                    title="Call"
                  >
                    <i className="mdi mdi-phone-outline fs-3"></i>
                  </a>
                  <a
                    href={`https://wa.me/${cleanNumber}`}
                    target="_blank"
                    rel="noreferrer"
                    title="WhatsApp"
                  >
                    <i className="mdi mdi-whatsapp fs-3"></i>
                  </a>
                  <a
                    href={
                      /Mobi|Android/i.test(navigator.userAgent)
                        ? `mailto:${email}`
                        : `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Send Email"
                  >
                    <i className="mdi mdi-email-send-outline fs-3"></i>
                  </a>

                </div>
              </div>
            );
          },
        },
        {
          header: "Pause Start Date",
          accessorKey: "Pause_Start_Date",
          enableSorting: true,
          enableColumnFilter: false,
          cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
        },
        {
          header: "Pause End Date",
          accessorKey: "Pause_End_Date",
          enableSorting: true,
          enableColumnFilter: false,
          cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
        },
        {
          header: "Reason",
          accessorKey: "Note",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Action",
          enableSorting: false,
          enableColumnFilter: false,
          cell: ({ row }) => {
            const rowData = row.original;

            return (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-2">
                  {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.VIEW) && (
                    <button className="btn btn-sm btn-primary" onClick={() => navigate(`/student/view/${rowData.Student_Id}`)}>View</button>
                  )}
                  {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.EDIT) && (
                    <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/student/edit/${rowData.Student_Id}`)}>Edit</button>
                  )}
                </div>
              </div>
            );
          },
        }
      ];
    }

    const baseColumns = [
      {
        header: "Student Name",
        meta: {
          sticky: true,
          width: "125px",
        },
        accessorKey: "Student_Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Parent Name",
        accessorKey: "Parent_Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Contact No",
        accessorKey: "Contact_Number",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const mobile = rowData.Contact_Number;
          const cleanNumber = mobile.replace(/\D/g, "");
          const email = rowData.Email_Address;

          return (
            <div className="d-flex flex-column gap-1">
              <span>{rowData.Contact_Number}</span>
              <div className="d-flex gap-2 align-items-center">
                <a
                  href={`tel:+${cleanNumber}`}
                  title="Call"
                >
                  <i className="mdi mdi-phone-outline fs-3"></i>
                </a>
                <a
                  href={`https://wa.me/${cleanNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  title="WhatsApp"
                >
                  <i className="mdi mdi-whatsapp fs-3"></i>
                </a>
                <a
                  href={
                    /Mobi|Android/i.test(navigator.userAgent)
                      ? `mailto:${email}`
                      : `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Send Email"
                >
                  <i className="mdi mdi-email-send-outline fs-3"></i>
                </a>

              </div>
            </div>
          );
        },
      },
      {
        header: "Email",
        accessorKey: "Email_Address",
        enableSorting: true,
        enableColumnFilter: false,
      },
    ];

    if (activeTab === "new") {
      return [
        ...baseColumns,
        {
          header: "Created Date",
          accessorKey: "CreatedOn",
          enableSorting: true,
          enableColumnFilter: false,
          cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
        },
        {
          header: "Action",
          enableSorting: false,
          enableColumnFilter: false,
          cell: ({ row }) => {
            const rowData = row.original;

            return (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-2">
                  {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.VIEW) && (
                    <button className="btn btn-sm btn-primary" onClick={() => navigate(`/student/view/${rowData.Id}`)}>View</button>
                  )}
                  {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.DELETE) && (
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(rowData)}>Delete</button>
                  )}
                </div>

                <div className="d-flex gap-2">
                  {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.EDIT) && (
                    <button className="btn btn-sm btn-info" onClick={() => handleApprove(rowData)}>Approve</button>
                  )}
                  {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.EDIT) && (
                    <button className="btn btn-sm btn-warning" onClick={() => handleReject(rowData)}>Reject</button>
                  )}
                </div>
              </div>
            );
          },
        }
      ];
    }

    if (activeTab === "rejected") {
      return [
        ...baseColumns,
        {
          header: "Created Date",
          accessorKey: "CreatedOn",
          enableSorting: true,
          enableColumnFilter: false,
          cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
        },
        {
          header: "Status",
          accessorKey: "IsActive",
          enableSorting: true,
          enableColumnFilter: false,
          cell: ({ row }) => {

            const isActive = row.original.IsActive === 1;
            const isApproved = row.original.IsApproved === 1;

            return (
              <div className="d-flex gap-2">
                <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
                  {isActive ? "Active" : "Inactive"}
                </span>
                <span className={`badge ${isApproved ? "bg-primary" : "bg-danger"}`}>
                  {isApproved ? "Approved" : "Rejected"}
                </span>
              </div>
            );
          },
        },
        {
          header: "Action",
          enableSorting: false,
          enableColumnFilter: false,
          cell: ({ row }) => {
            const rowData = row.original;

            return (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-2">
                  {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.VIEW) && (
                    <button className="btn btn-sm btn-primary" onClick={() => navigate(`/student/view/${rowData.Id}`)}>View</button>
                  )}
                  {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.EDIT) && (
                    <button className="btn btn-sm btn-warning" onClick={() => handleRevert(rowData)}>Revert</button>
                  )}
                </div>
              </div>
            );
          },
        }
      ];
    }

    return [
      ...baseColumns,
      {
        header: "Duration",
        accessorKey: "Class_Duration",
        cell: ({ getValue }) => {
          const value = getValue();
          switch (value) {
            case 1:
              return "Monthly";
            case 3:
              return "Quarterly";
            default:
              return "N/A";
          }
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Class Day",
        accessorKey: "Day_Of_Week",
        cell: ({ getValue }) => getValue() || "N/A",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Class Time",
        accessorKey: "Time",
        cell: ({ getValue }) => getValue() || "N/A",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Created Date",
        accessorKey: "CreatedOn",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
      },
      {
        header: "Status",
        accessorKey: "IsActive",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const isActive = row.original.IsActive === 1;
          const isApproved = row.original.IsApproved === 1;
          const isPlanExpired = row.original.Plan_IsActive === null;
          const startDate = new Date(row.original.Start_Date);
          const isStudent = startDate.getDate() !== 1;

          return (
            <>
              <div className="d-flex gap-2">
                <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
                  {isActive ? "Active" : "Inactive"}
                </span>
                <span className={`badge ${isApproved ? "bg-success" : "bg-danger"}`}>
                  {isApproved ? "Approved" : "Rejected"}
                </span>
              </div>
              <div className="d-flex gap-2 mt-2">
                {isStudent && (
                  <span className="badge bg-black text-white fs-6">Midway Student</span>
                )}
              </div>
              <div className="d-flex gap-2 mt-2">
                {isPlanExpired && (
                  <span className="badge bg-primary text-white fs-6">Plan Expired</span>
                )}
              </div>
            </>
          );
        },
      },
      {
        header: "Login Allow?",
        accessorKey: "LoginAllow",
        enableSorting: true,
        enableColumnFilter: false,

        cell: ({ row }) => {
          const rowData = row.original;
          const isPlanExpired = row.original.Plan_IsActive === null;

          return (
            <div className="d-flex align-items-center gap-2">
              <Switch
                checked={Boolean(rowData.LoginAllow)}
                onChange={(checked) =>
                  handleStatusToggle(rowData, checked)
                }
                onColor="#626ed4"
                uncheckedIcon={<Offsymbol />}
                checkedIcon={<OnSymbol />}
                disabled={!isPlanExpired}
              />
            </div>
          );
        },
      },
      {
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;

          return (
            <div className="d-flex flex-column gap-2">
              <div className="d-flex gap-2">
                {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.VIEW) && (
                  <button className="btn btn-sm btn-primary" onClick={() => navigate(`/student/view/${rowData.Id}`)}>View</button>
                )}
                {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.EDIT) && (
                  <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/student/edit/${rowData.Id}`)}>Edit</button>
                )}
                {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.DELETE) && (
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(rowData)}>Delete</button>
                )}
                {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.VIEW) && (
                  <button className="btn btn-sm btn-info" onClick={() => navigate(`/student/history/${rowData.Id}`)}>History</button>
                )}
              </div>

              <div className="d-flex gap-2">
                {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.EDIT) && (
                  <button className="btn btn-sm btn-warning" onClick={() => handleComp(rowData)}>Add Comp</button>
                )}
                {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.EDIT) && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      setSelectedStudentId(rowData.Id);
                      setShowMoveModal(true);
                    }}
                  >
                    Move Student
                  </button>
                )}
              </div>
            </div>
          );
        },
      }
    ];
  }, [activeTab, handleApprove, handleReject, handleDelete]);

  //meta title
  document.title = "Students | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Students"
          breadcrumbItems={[
            { title: "Students" }
          ]}
        />

        {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.INSERT) && (
          <div className="d-flex justify-content-end mb-3">
            <button className="btn btn-primary mb-3" onClick={() => navigate("/student/create")}>
              + Create Student
            </button>
          </div>
        )}

        {hasPermission(permissionMap, MODULES.STUDENTS, PERMISSIONS.VIEW) && (
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <button
                className={`btn ${activeTab === "all" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setActiveTab("all")}
              >
                All Students
              </button>

              <button
                className={`btn ${activeTab === "new" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setActiveTab("new")}
              >
                New Requests
              </button>

              <button
                className={`btn ${activeTab === "pause" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setActiveTab("pause")}
              >
                Pause Students
              </button>

              <button
                className={`btn ${activeTab === "rejected" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setActiveTab("rejected")}
              >
                Rejected Students
              </button>
            </div>

            {activeTab === "all" && (
              <div className="mb-3 text-end">
                {role === "Super Admin" && (
                  <>
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      disabled={importing}
                      onClick={() => fileInputRef.current.click()}
                    >
                      <i className="mdi mdi-file-import me-1"></i>
                      {importing ? "Importing..." : "Import From Excel"}
                    </button>

                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".xlsx,.xls"
                      style={{ display: "none" }}
                      onChange={handleImportExcel}
                    />
                  </>
                )}
                <button
                  type="button"
                  className="btn btn-success me-2"
                  onClick={exportToExcel}
                >
                  <i className="mdi mdi-microsoft-excel me-1"></i>  Export Excel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={exportToPDF}
                >
                  <i className="mdi mdi-file-pdf-outline me-1"></i>  Export PDF
                </button>
              </div>
            )}
          </div>
        )}

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          countPerPage={countPerPage}
          totalRecords={totalRecords}
          sortingState={sortingState}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          searchValue={search}
          onSearch={handleSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          isLoading={loading}
          isPagination
          isGlobalFilter
          isCustomPageSize
          SearchPlaceholder="search..."
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <MoveStudentModal
          show={showMoveModal}
          studentId={selectedStudentId}
          onClose={() => {
            setShowMoveModal(false);
            setSelectedStudentId(null);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      </div>
    </div>
  );
};

export default DatatableTables;
